/**
 * Active-workspace store + cross-tab subscription primitive (IA-2.2 +
 * IA-2.3).
 *
 * The IA Phase 2 redesign — founder pick of Candidate A from the IA
 * research at `26664e8` — makes the active workspace a top-level chrome
 * concept. The `<WorkspaceSwitcher/>` pill (IA-2.2) reads + writes this
 * value so a multi-team lead has a persistent "where I am right now"
 * anchor; route restoration (IA-2.3) reads it on first mount so a
 * returning user lands on their last-active workspace.
 *
 * Persistence: localStorage at v0.1 per the implementation plan §4.5.
 * No server-side `last_active_team_id` column yet — promote when a
 * real lead asks for cross-device parity. The chosen storage key
 * (`tarii.active_workspace_id`) is namespaced under "tarii." per the
 * brand-string lock (founder §4.1) and is distinct from the legacy
 * `roster.dev_account_id` + `roster.dev_cycle_id` keys.
 *
 * Cross-tab consistency: writers emit a `tarii:workspace-changed`
 * window event (in-tab) AND rely on the native browser `storage`
 * event (cross-tab; fired automatically when localStorage changes
 * from another tab in the same origin). `subscribeToWorkspaceChanges`
 * folds both into a single subscription so consumers don't have to
 * juggle two event paths.
 *
 * Resolver: `resolveInitialWorkspace` implements the §4.5
 * precedence — deep-link → localStorage → most-recent → null. The
 * recency ordering is server-side per IA-2.1 (`044ffd0`); the resolver
 * just walks the (already-ordered) array.
 *
 * Privacy posture: this module never holds a workspace name or any
 * Circle-2 data — only the team UUID. Names / role chips / plan tier
 * come from the live `useWorkspaces` query, which is keyed by
 * `accountId` (`workspacesKey`) so no cross-account leakage is possible.
 */

const STORAGE_KEY = "tarii.active_workspace_id";
const CHANGE_EVENT = "tarii:workspace-changed";

/** Read the currently-active workspace id from localStorage. Returns
 *  null in SSR / non-browser environments and when nothing has been
 *  set yet. */
export function getActiveWorkspaceId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Some environments throw on localStorage access (private mode in
    // older Safari builds; sandboxed iframes). Treat as "no value" so
    // the switcher falls through to the most-recently-active workspace.
    return null;
  }
}

/** Write the active workspace id to localStorage and emit the change
 *  event so subscribers re-render. Pass null to clear the value (used
 *  by the IA-2.3 stale-storage clean-up path). */
export function setActiveWorkspaceId(id: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (id == null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, id);
    }
  } catch {
    // Fall through silently — the in-tab event still fires below so
    // the current tab's UI updates even if persistence failed.
  }
  // Same-tab subscribers don't see the native `storage` event; emit
  // a custom event so they re-render too.
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { id } }));
  } catch {
    // CustomEvent unavailable in some test envs without jsdom; ignore.
  }
}

/** Subscribe to workspace-id changes from any source (this tab via
 *  `setActiveWorkspaceId`, other tabs via the native `storage` event).
 *  Returns the unsubscribe function. */
export function subscribeToWorkspaceChanges(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onCustom = () => cb();
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) cb();
  };
  window.addEventListener(CHANGE_EVENT, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}

// ─── Resolver (IA-2.3) ───────────────────────────────────────────────

/** Minimal shape the resolver consumes. Mirrors `WorkspaceRow` from
 *  `schema.gen.ts` but typed structurally so unit tests don't need to
 *  fabricate the full row.
 *
 *  The array is assumed to be ordered most-recently-active first
 *  (IA-2.1 backend ranks server-side by
 *  `last_membership_event_at DESC NULLS LAST, team_name ASC`). Per
 *  the §4.1 cache-shape note, consumers MUST iterate in order and
 *  never `.find(...)`-by-id-then-reposition. */
export interface WorkspaceLike {
  team_id: string;
}

/** Workspace role union as returned by the IA-2.1 endpoint. Mirrors
 *  the `WorkspaceRow.role` enum in `schema.gen.ts`. Re-exported here
 *  so the IA-2.5 AppShell role-aware-gate can derive the right-side
 *  strip shape without importing the heavy `schema.gen.ts` module. */
export type WorkspaceRole = "lead" | "co_lead" | "member";

/** Minimal shape the role selector consumes. Structural typing keeps
 *  the IA-2.5 AppShell tests free of full `WorkspaceRow` fixtures. */
export interface WorkspaceLikeWithRole extends WorkspaceLike {
  role: WorkspaceRole;
}

/**
 * Resolve which workspace to render as active on first mount, per the
 * implementation plan §4.5 precedence:
 *
 *   1. Deep-link wins. If `deepLinkTeamId` is non-null AND the team
 *      id appears in the workspaces array, use it.
 *   2. Otherwise, read `localStorage["tarii.active_workspace_id"]`.
 *      If the stored id still appears in the workspaces list, use it.
 *   3. Otherwise, the most-recently-active workspace from the server
 *      ordering (i.e. `workspaces[0]`).
 *   4. Otherwise (empty memberships → `workspaces.length === 0`),
 *      return null. The HomePage handles the empty case as today.
 *
 * The resolver is pure — it does NOT write to localStorage on its own.
 * Callers that want the stored value cleared on case (3) when the
 * stored id is stale must call `setActiveWorkspaceId(null)` themselves
 * (the AppShell does this once on first mount per IA-2.3).
 */
export function resolveInitialWorkspace(
  workspaces: ReadonlyArray<WorkspaceLike>,
  deepLinkTeamId: string | null,
): string | null {
  if (workspaces.length === 0) return null;

  // (1) Deep-link wins.
  if (deepLinkTeamId) {
    const match = workspaces.find((w) => w.team_id === deepLinkTeamId);
    if (match) return match.team_id;
  }

  // (2) localStorage restore — only if still in the membership list.
  const stored = getActiveWorkspaceId();
  if (stored && workspaces.some((w) => w.team_id === stored)) {
    return stored;
  }

  // (3) Fall through to the most-recently-active workspace.
  const head = workspaces[0];
  return head ? head.team_id : null;
}

// ─── Role selector (IA-2.5) ─────────────────────────────────────────

/**
 * IA-2.5 — return the caller's role on the active workspace.
 *
 * Used by `<AppShell/>` to derive the right-side strip shape per the
 * implementation plan §3 IA-2.5: `member` → member strip (Cycles /
 * My schedule / ...); `lead` or `co_lead` → lead strip
 * (Cycles / Members / Setup).
 *
 * Returns null when:
 *   - workspaces is null/undefined (query still loading or 401)
 *   - activeWorkspaceId is null (empty memberships, or first paint
 *     before the AppShell first-mount resolver writes it)
 *   - the active id is not in the workspaces array (stale localStorage
 *     entry — the IA-2.3 first-mount resolver clears it on the next
 *     mount; in the meantime AppShell falls back to the no-strip
 *     branch, which matches the empty-memberships case)
 *
 * Pure: no localStorage reads here — the active id flows in as a
 * parameter so consumers can subscribe to changes via
 * `subscribeToWorkspaceChanges` and re-derive the role without this
 * helper firing a stale read on the side.
 */
export function getActiveWorkspaceRole(
  workspaces: ReadonlyArray<WorkspaceLikeWithRole> | null | undefined,
  activeWorkspaceId: string | null,
): WorkspaceRole | null {
  if (!workspaces || !activeWorkspaceId) return null;
  const active = workspaces.find((w) => w.team_id === activeWorkspaceId);
  return active?.role ?? null;
}

/**
 * The role `<MemberWelcomeGate/>` asks about: the one the workspace the
 * AppShell WOULD resolve to carries (30 Sep 2026 re-click).
 *
 * The gate used to read `tarii.active_workspace_id` raw and fall through
 * when it was empty, trusting "the IA-2.3 first-mount resolver inside
 * AppShell will pick one and the next render will re-evaluate". For a
 * member that never happens: the landing gate sends a member-only account
 * from `/` straight to `/me`, whose phone frame does not mount AppShell, so
 * nothing ever writes the key — and a fresh browser, or any browser after a
 * sign-out (#1661 clears the key), skipped the first-run welcome entirely.
 *
 * Resolving here with the same precedence the AppShell uses (a `/teams/:id`
 * deep link → the stored id if still valid → the most-recently-active
 * workspace) makes the gate's answer independent of which surface happened
 * to write the key first. Pure apart from the localStorage read inside
 * `resolveInitialWorkspace`; it never writes.
 */
export function resolveWelcomeGateRole(
  workspaces: ReadonlyArray<WorkspaceLikeWithRole> | null | undefined,
  pathname: string,
): WorkspaceRole | null {
  if (!workspaces || workspaces.length === 0) return null;
  const deepLink = pathname.match(/^\/teams\/([0-9a-f-]{36})(?:\/|$)/i)?.[1] ?? null;
  return getActiveWorkspaceRole(workspaces, resolveInitialWorkspace(workspaces, deepLink));
}
